import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Observable, Subscription, finalize, take } from 'rxjs';
import { PremiseHazardType } from '@premise-hazard-type-models/premise-hazard-type.model';
import { GetPremiseHazardTypesRequestModel } from '@premise-hazard-type-models/get-premise-hazard-type-request.model';
import { PremiseHazardQueryRestService } from '../../services/rest/premise-hazard-rest.service';
import { BaseListComponent, PaginatedResponse } from '../../shared/base-list.component';
import { ResponseItem } from '@models/response-item.model';

@Component({
    selector: 'app-premise-hazard-type-list',
    templateUrl: './premise-hazard-type-list.component.html',
    styleUrls: ['./premise-hazard-type-list.component.scss'],
    standalone: false, 
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PremiseHazardTypeListComponent extends BaseListComponent<PremiseHazardType> implements OnInit, OnDestroy {
  @Output() PremiseHazardTypeClicked: EventEmitter<PremiseHazardType> = new EventEmitter<PremiseHazardType>();
  @Output() AddNewPremiseHazardTypeClicked: EventEmitter<void> = new EventEmitter<void>();
  @Input() premiseHazardTypeDeletedSubject!: Observable<PremiseHazardType>;
  @Input() changeSelectedPremiseHazardType!: Observable<ResponseItem<PremiseHazardType> | undefined>;

  private changeSelectedPremiseHazardTypeSubscription!: Subscription;
  private premiseHazardTypeDeletedSubjectSubscription!: Subscription;

  // Expose displayItems as PremiseHazardTypes for template compatibility
  get PremiseHazardTypes(): PremiseHazardType[] | undefined {
    return this.displayItems as PremiseHazardType[] | undefined;
  }

  // Expose SelectedItem as SelectedPremiseHazardType for template compatibility
  get SelectedPremiseHazardType(): PremiseHazardType | undefined {
    return this.SelectedItem;
  }
  set SelectedPremiseHazardType(value: PremiseHazardType | undefined) {
    this.SelectedItem = value;
  }

  constructor(
    private restService: PremiseHazardQueryRestService,
    transloco: TranslocoService,
    cdr: ChangeDetectorRef
  ) {
    super(transloco, cdr);
  }

  override ngOnInit(): void {
    this.loadTranslations();
    this.loadItems();
    this.changeSelectedPremiseHazardTypeSubscription = this.changeSelectedPremiseHazardType
      .subscribe(hazardTypeIn => {
        this.onChangeSelectedPremiseHazardType(hazardTypeIn);
      });
    this.premiseHazardTypeDeletedSubjectSubscription = this.premiseHazardTypeDeletedSubject
      .subscribe(hazardType => {
        this.onPremiseHazardTypeDeleted(hazardType);
      });
  }

  public ngOnDestroy(): void {
    if (this.changeSelectedPremiseHazardTypeSubscription) {
      this.changeSelectedPremiseHazardTypeSubscription.unsubscribe();
    }
    if (this.premiseHazardTypeDeletedSubjectSubscription) {
      this.premiseHazardTypeDeletedSubjectSubscription.unsubscribe();
    }
  }

  private onPremiseHazardTypeDeleted(premiseHazardType: PremiseHazardType): void {
    this.onItemDeleted(premiseHazardType);
  }

  public addNewClicked(): void {
    if (!this.AllowInteraction) {
      return;
    }
    this.AddNewPremiseHazardTypeClicked.emit();
  }

  private onChangeSelectedPremiseHazardType(hazardTypeIn: ResponseItem<PremiseHazardType> | undefined): void {
    if (!hazardTypeIn) {
      this.itemClicked({ index: this.SelectionIndex });
      return;
    }
    this.AllowInteraction = false;
    this.restService.getPremiseHazardType(hazardTypeIn.Item!.id)
      .pipe(take(1), finalize(() => this.AllowInteraction = true))
      .subscribe(response => {
        if (this.itemsDictionary.has(response.Item!.id)) {
          let idx = 0;
          for (const [key, _] of this.itemsDictionary) {
            if (key === response.Item!.id) {
              this.items[idx] = response.Item!;
              this.itemClicked({ index: idx });
              break;
            }
            idx++;
          }
        } else {
          this.itemsDictionary.set(response.Item!.id, response.Item!);
          this.items.push(response.Item!);
          this.updateDisplayItems();
          this.itemClicked({ index: this.items.length - 1 });
        }
      });
  }

  protected loadItems(callback?: () => void): void {
    let request: GetPremiseHazardTypesRequestModel = {
      continuationToken: this.continuationToken || undefined,
      maxItemCount: 100,
      includeSharedData: true,
    };
    this.restService.getPremiseHazardTypes(request)
      .pipe(take(1))
      .subscribe(response => {
        const paginatedResponse: PaginatedResponse<PremiseHazardType> = {
          continuationToken: response.continuationToken,
          items: response.premiseHazardTypes
        };
        this.processResponse(paginatedResponse, callback);
      });
  }

  protected onItemSelected(item: PremiseHazardType): void {
    this.PremiseHazardTypeClicked.emit(item);
  }

  // Expose itemClicked as premiseHazardTypeClicked for template compatibility
  public premiseHazardTypeClicked(event: any): void {
    this.itemClicked(event);
  }
}
